import {
  sanitizeTelemetry,
  sanitizeTelemetryUrl,
} from '@borradh-workspace/observability/telemetry-sanitizer';
import type { CaptureResult } from 'posthog-js';

import { getBrowserErrorEndpoint } from './browser-error-endpoint';
import { sanitizeBrowserTelemetry } from './browser-telemetry';

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 1_000;
// `$pageview` / `$pageleave` / `$web_vitals` stay in PostHog: they were ~80% of
// this source's volume and carry no incident signal web analytics lacks.
const KEEP_POSTHOG_SYSTEM_EVENTS = new Set(['$rageclick']);
type BrowserLogLevel = 'debug' | 'info' | 'warn' | 'error';

interface Queue {
  events: Array<Record<string, unknown>>;
  timer?: ReturnType<typeof setTimeout>;
}

const queues = new Map<string, Queue>();

/**
 * A 4xx is the API answering correctly (no permission, no such row, bad
 * input) — only a 5xx is a fault. Logging both as `error` buried the real
 * failures under routine 403/404s.
 */
export function levelForHttpStatus(status: number): BrowserLogLevel {
  return status >= 500 ? 'error' : 'warn';
}

const MIRROR_INSTALLED = '__borradh_betterstack_browser_mirror_installed__';

function flush(endpoint: string, keepalive = false): void {
  const queue = queues.get(endpoint);
  if (!queue || queue.events.length === 0) return;
  if (queue.timer) clearTimeout(queue.timer);
  queue.timer = undefined;

  const events = queue.events.splice(0, queue.events.length);
  void fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(sanitizeTelemetry(events)),
    // `keepalive` requests share a browser-wide 64 KiB budget. Normal
    // scheduled batches do not need it; reserve it for page unload.
    keepalive,
  }).catch(() => undefined);
}

function toJsonValue(
  value: unknown,
  seen = new WeakSet<object>(),
  depth = 0
): unknown {
  if (value === null || value === undefined) return value;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return value;
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (depth >= 6) return '[TRUNCATED]';
  if (Array.isArray(value))
    return value
      .slice(0, 100)
      .map((item) => toJsonValue(item, seen, depth + 1));
  if (typeof value === 'object') {
    if (seen.has(value)) return '[CIRCULAR]';
    seen.add(value);
    try {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .slice(0, 100)
          .map(([key, item]) => [key, toJsonValue(item, seen, depth + 1)])
      );
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function enqueue(
  event: string,
  properties: Record<string, unknown>,
  source = 'browser-console-and-network-forwarder',
  level: BrowserLogLevel = 'info',
  msg = event
): void {
  const endpoint = getBrowserErrorEndpoint();
  let queue = queues.get(endpoint);
  if (!queue) {
    queue = { events: [] };
    queues.set(endpoint, queue);
    window.addEventListener('pagehide', () => flush(endpoint, true));
  }
  queue.events.push(
    sanitizeTelemetry({
      level,
      msg,
      // Better Stack's list view promotes `message`; keep Pino's `msg` too for
      // tools that expect its conventional field name.
      message: msg,
      event,
      properties,
      source,
      service: 'dashboard-app',
      runtime: 'browser',
      dt: new Date().toISOString(),
    })
  );
  if (queue.events.length >= BATCH_SIZE) flush(endpoint);
  else if (!queue.timer)
    queue.timer = setTimeout(() => flush(endpoint), FLUSH_INTERVAL_MS);
}

/** Installs once: console warnings/errors, resource failures, and failed fetches. */
export function installBrowserConsoleAndNetworkMirroring(): void {
  const target = window as typeof window & Record<string, boolean | undefined>;
  if (target[MIRROR_INSTALLED]) return;
  target[MIRROR_INSTALLED] = true;

  for (const level of ['warn', 'error'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      enqueue(
        `console.${level}`,
        { args: sanitizeTelemetry(args.map((arg) => toJsonValue(arg))) },
        'browser-console-and-network-forwarder',
        level,
        typeof args[0] === 'string' ? args[0] : `console.${level}`
      );
      original(...args);
    };
  }

  window.addEventListener(
    'error',
    (event) => {
      const element = event.target;
      if (!(element instanceof HTMLElement)) return;
      enqueue(
        'resource.load_failed',
        {
          tag: element?.tagName,
          url: element?.getAttribute('src') ?? element?.getAttribute('href'),
        },
        'browser-console-and-network-forwarder',
        'error',
        'Resource failed to load'
      );
    },
    true
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const url =
      typeof args[0] === 'string'
        ? args[0]
        : args[0] instanceof Request
          ? args[0].url
          : args[0].toString();
    try {
      const response = await originalFetch(...args);
      if (!response.ok && !url.includes('/api/browser-error')) {
        enqueue(
          'network.fetch_failed',
          {
            url,
            status: response.status,
            statusText: response.statusText,
          },
          'browser-console-and-network-forwarder',
          levelForHttpStatus(response.status),
          `HTTP ${response.status} ${sanitizeTelemetryUrl(url)}`
        );
      }
      return response;
    } catch (error) {
      if (!url.includes('/api/browser-error')) {
        enqueue(
          'network.fetch_failed',
          { url, error: toJsonValue(error) },
          'browser-console-and-network-forwarder',
          'error',
          `Fetch failed: ${sanitizeTelemetryUrl(url)}`
        );
      }
      throw error;
    }
  };
}

/**
 * Mirrors every accepted non-exception PostHog browser event to Better Stack.
 * Raw replay/autocapture data remains in PostHog: Better Stack keeps the
 * compact operational timeline used to diagnose a frontend incident.
 */
export function shouldMirrorPostHogEvent(eventName: string): boolean {
  return (
    !eventName.startsWith('$') || KEEP_POSTHOG_SYSTEM_EVENTS.has(eventName)
  );
}

export function mirrorPostHogEvent(
  event: CaptureResult | null,
  projectToken?: string
): CaptureResult | null {
  if (!event || !shouldMirrorPostHogEvent(event.event)) return event;

  enqueue(
    event.event,
    sanitizeTelemetry(event.properties),
    'posthog-browser-event-forwarder',
    'info',
    event.event
  );

  return sanitizeBrowserTelemetry(event, projectToken);
}
